import { Box, Chip, FormControl, IconButton, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { Check, Download, Edit } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { PageLayout } from "../../layouts/PageLayout";
import { handlePdfDownload, Listing } from "../../components/pdf";
import { ActionButton, CardLayout, FormDialog, MTable, PageHeader, SelectField } from "../../components/v3";
import {
  ASSIGN_WEEKLY_WORKER_PAYMENT_SUPERVISOR,
  EXPORT_WEEKLY_WORKER_PAYMENT,
  PAY_WEEKLY_WORKER_PAYMENT,
  VIEW_ACCOUNT,
  VIEW_WORKER_WELFARE,
} from "../../constants";
import { AccountService, HumanResourceService, UserService } from "../../services";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { getAmountToPayColumns } from "./constants";

type PaymentStatus = "UNPAID" | "PARTIALLY_PAID" | "PAID";

const STATUS_CHIP: Record<PaymentStatus, { label: string; color: "default" | "warning" | "success" }> = {
  UNPAID: { label: "Unpaid", color: "default" },
  PARTIALLY_PAID: { label: "Partially Paid", color: "warning" },
  PAID: { label: "Paid", color: "success" },
};

// A weekly payment can span several projects — each one is reviewed and paid independently here,
// rather than the session being paid as a single all-or-nothing unit (see AJCWLT follow-up).
export const WeeklyPaymentReport = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state: any) => state.user);
  const { ownerUserId: companyAdminId } = user.company;
  const hasViewAccount = useHasPermission(VIEW_ACCOUNT);
  const canPay = useHasPermission(PAY_WEEKLY_WORKER_PAYMENT);
  const canAssignSupervisor = useHasPermission(ASSIGN_WEEKLY_WORKER_PAYMENT_SUPERVISOR);
  const canExport = useHasPermission(EXPORT_WEEKLY_WORKER_PAYMENT);
  const canViewWelfare = useHasPermission(VIEW_WORKER_WELFARE);

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  // Keyed by user id. Every project's saved disbursedByUserId is batch-resolved into this once
  // per session load (see the effect below) — the session only ever gives back ids, not names.
  const [resolvedUsers, setResolvedUsers] = useState<Record<number, any>>({});
  const [selectedProjectKey, setSelectedProjectKey] = useState<string>("");
  const [payPanel, setPayPanel] = useState<Record<string, { fromAccountId?: any; disbursedByUserId?: any }>>({});
  const [editingSupervisor, setEditingSupervisor] = useState(false);
  const [paying, setPaying] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const fetchSession = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await HumanResourceService.getWeeklyWorkPaymentSession(sessionId);
      setItems(data?.payments || []);
    } catch (error) {
      console.error("Error fetching weekly payment session:", error);
      toast.error("Failed to load weekly payment session");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const formatUserName = (u: any) => (u ? `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.name || `User #${u.id}` : null);

  const projects = useMemo(() => {
    const map = new Map<string, { key: string; projectId: number | null; name: string }>();
    items.forEach((item: any) => {
      const key = item.projectId != null ? String(item.projectId) : "none";
      if (!map.has(key)) {
        map.set(key, { key, projectId: item.projectId ?? null, name: item.project?.name || "No Project" });
      }
    });
    return Array.from(map.values());
  }, [items]);

  useEffect(() => {
    if (!selectedProjectKey && projects.length) setSelectedProjectKey(projects[0].key);
  }, [projects, selectedProjectKey]);

  const selectedProject = projects.find((p) => p.key === selectedProjectKey);

  const rows = useMemo(
    () =>
      items
        .filter((item: any) => (item.projectId != null ? String(item.projectId) : "none") === selectedProjectKey)
        .map((item: any) => ({
          id: item.id,
          workerId: item.workerId,
          name: item.worker?.name || `Worker #${item.workerId}`,
          balance: Number(item.openingBalance) || 0,
          workHours: item.noOfDays != null ? Number(item.noOfDays) : undefined,
          totalWorked: Number(item.totalWage) || 0,
          overtime: Number(item.totalOvertime) || 0,
          advancePaid: Number(item.advancePaid) || 0,
          welfare: Number(item.deduction?.welfare) || 0,
          deduction: Number(item.deduction?.otherDeduction) || 0,
          amountToPay: Number(item.paymentAmount) || 0,
          closingBalance: Number(item.closingBalance) || 0,
          isPaid: Boolean(item.transactionId),
          disbursedByUserId: item.meta?.disbursedByUserId,
        })),
    [items, selectedProjectKey],
  );

  const totalAmount = useMemo(() => rows.reduce((sum, row) => sum + Number(row.amountToPay || 0), 0), [rows]);
  const unpaidRowCount = useMemo(() => rows.filter((row) => !row.isPaid).length, [rows]);

  const status: PaymentStatus = useMemo(() => {
    if (!rows.length) return "UNPAID";
    if (rows.every((row) => row.isPaid)) return "PAID";
    if (rows.some((row) => row.isPaid)) return "PARTIALLY_PAID";
    return "UNPAID";
  }, [rows]);

  // disbursedByUserId can be set independently of payment (see setProjectSupervisor) — any row
  // carrying it, paid or not, is the saved responsible user for that row's project.
  const savedDisbursedByUserId = useMemo(() => rows.find((row) => row.disbursedByUserId)?.disbursedByUserId, [rows]);

  // Resolve every project's saved supervisor in one batched call, skipping ids already in the
  // cache — e.g. one just saved via handleSaveSupervisor, which updates the cache itself — so
  // re-fetching the session (fetchSession) after a save never re-requests a user we already have.
  useEffect(() => {
    const uniqueIds = [...new Set(items.map((item: any) => item.meta?.disbursedByUserId).filter(Boolean))];
    const missingIds = uniqueIds.filter((id) => !resolvedUsers[id]);
    if (!missingIds.length) return;
    UserService.getUsers({ ids: missingIds, limit: missingIds.length })
      .then(({ data }: any) => {
        const rowsData = data?.rows || data || [];
        setResolvedUsers((prev) => {
          const next = { ...prev };
          rowsData.forEach((u: any) => {
            next[u.id] = u;
          });
          return next;
        });
      })
      .catch((error) => console.error("Error fetching users:", error));
  }, [items, resolvedUsers]);

  const panel = payPanel[selectedProjectKey] || {};
  const setPanelField = (field: "fromAccountId" | "disbursedByUserId", value: any) =>
    setPayPanel((prev) => ({ ...prev, [selectedProjectKey]: { ...prev[selectedProjectKey], [field]: value } }));

  // A freshly-picked (not yet saved) responsible user is the full object from SelectField, so its
  // name is already in hand; a previously-saved one only gives back an id, resolved above.
  const supervisorName = panel.disbursedByUserId
    ? formatUserName(panel.disbursedByUserId)
    : savedDisbursedByUserId
      ? formatUserName(resolvedUsers[savedDisbursedByUserId]) || `User #${savedDisbursedByUserId}`
      : null;

  useEffect(() => {
    setEditingSupervisor(false);
  }, [selectedProjectKey]);

  const [payConfirmOpen, setPayConfirmOpen] = useState(false);

  const handlePayClick = () => {
    if (!panel.fromAccountId?.id) {
      toast.error("Select an account to pay from");
      return;
    }
    setPayConfirmOpen(true);
  };

  // Pay only ever pays — who's responsible is recorded separately via the inline Supervisor
  // editor's tick (handleSaveSupervisor), not bundled into this call.
  const handlePay = async () => {
    setPayConfirmOpen(false);
    setPaying(true);
    try {
      await HumanResourceService.payWeeklyWorkPaymentProject(sessionId, {
        projectId: selectedProject?.projectId ?? undefined,
        fromAccountId: panel.fromAccountId.id,
      });
      toast.success("Project paid successfully");
      await fetchSession();
    } catch (error: any) {
      const response = error?.response?.data;
      const message = Array.isArray(response?.message) ? response.message.join(", ") : response?.message || "Failed to pay";
      toast.error(message);
    } finally {
      setPaying(false);
    }
  };

  const [savingSupervisor, setSavingSupervisor] = useState(false);

  const handleSaveSupervisor = async () => {
    if (!panel.disbursedByUserId?.id) {
      toast.error("Select a responsible user");
      return;
    }
    setSavingSupervisor(true);
    try {
      await HumanResourceService.setWeeklyWorkPaymentProjectSupervisor(sessionId, {
        projectId: selectedProject?.projectId ?? undefined,
        disbursedByUserId: panel.disbursedByUserId.id,
      });
      // Already have the full user object from the SelectField selection — cache it locally
      // instead of letting the next fetchSession()-triggered resolve effect re-fetch it.
      setResolvedUsers((prev) => ({ ...prev, [panel.disbursedByUserId.id]: panel.disbursedByUserId }));
      toast.success("Responsible user saved");
      setEditingSupervisor(false);
      await fetchSession();
    } catch (error: any) {
      const response = error?.response?.data;
      const message = Array.isArray(response?.message) ? response.message.join(", ") : response?.message || "Failed to save";
      toast.error(message);
    } finally {
      setSavingSupervisor(false);
    }
  };

  const reportColumns = useMemo(
    () =>
      getAmountToPayColumns({ canViewWelfare })
        .filter((col: any) => col.field !== "projectId")
        .map((col: any) => ({ ...col, editable: false })),
    [canViewWelfare],
  );

  const handlePdfDownloadClick = async () => {
    setIsPdfGenerating(true);
    try {
      const meta = [
        { label: "Project", value: selectedProject?.name || "—" },
        ...(supervisorName ? [{ label: "Supervisor", value: supervisorName }] : []),
      ];
      const pdfColumnHeaders = derivePdfColumns(reportColumns);
      const printableRows = getPrintableRows(rows);
      const totals = { amountToPay: `₹ ${totalAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}` };

      const pdfDocument = (
        <Listing
          title="Weekly Payment Report"
          columnHeaders={pdfColumnHeaders}
          tableRows={printableRows}
          company={user?.company}
          meta={meta}
          totals={totals}
        />
      );

      await handlePdfDownload({ document: pdfDocument, pdfTitle: `Weekly-Payment-Report-${selectedProject?.name || ""}` });
    } catch {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const statusChip = STATUS_CHIP[status];

  return (
    <PageLayout>
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <PageHeader
          title="Weekly Payment Report"
          subtitle={`Session: ${sessionId}`}
          buttons={[{ buttonText: "Back", onClick: () => navigate(-1) }]}
        />
      </Box>

      <Box sx={{ mt: -9, zIndex: 2, position: "relative" }}>
        <CardLayout>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <InputLabel>Project</InputLabel>
              <Select label="Project" value={selectedProjectKey} onChange={(e) => setSelectedProjectKey(e.target.value)}>
                {projects.map((p) => (
                  <MenuItem key={p.key} value={p.key}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {canExport && (
              <ActionButton
                buttonIcon={Download}
                buttonText="Download PDF"
                variant="outlined"
                onButtonClick={handlePdfDownloadClick}
                loading={isPdfGenerating}
                disabled={!selectedProject}
              />
            )}
          </Box>
        </CardLayout>

        {selectedProject && (
          <CardLayout>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6">{selectedProject.name}</Typography>
              <Chip label={statusChip.label} color={statusChip.color} size="small" />
            </Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Typography variant="body2" color="text.secondary">
                Supervisor:
              </Typography>
              {editingSupervisor ? (
                <>
                  <SelectField
                    size="small"
                    value={panel.disbursedByUserId || null}
                    getData={UserService.getUsers}
                    searchField="searchText"
                    optionValueFields={["firstName", "lastName"]}
                    onChange={(selected: any) => setPanelField("disbursedByUserId", selected)}
                    style={{ width: 260 }}
                  />
                  <IconButton size="small" onClick={handleSaveSupervisor} disabled={savingSupervisor}>
                    <Check fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {supervisorName || "Not yet assigned"}
                  </Typography>
                  {status !== "PAID" && canAssignSupervisor && (
                    <IconButton size="small" onClick={() => setEditingSupervisor(true)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  )}
                </>
              )}
            </Box>

            <MTable
              columnHeaders={reportColumns}
              tableRows={rows}
              loading={loading}
              hideFooter
              autoHeight
              selectedFilter={undefined}
              filterOptions={[]}
              searchText=""
              onExportPdfClick={() => {}}
              paginationModel={{ page: 0, pageSize: rows.length || 1 }}
            />

            <Box mt={2} p={1.5} display="flex" justifyContent="flex-end" sx={{ background: "#F7F7FC", borderRadius: "8px" }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Total Amount: ₹ {totalAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </Typography>
            </Box>

            {status !== "PAID" && canPay && (
              <Box display="flex" justifyContent="flex-end" alignItems="flex-end" gap={2} mt={2}>
                <SelectField
                  label="From Account"
                  value={panel.fromAccountId || null}
                  getData={AccountService.getAdminAndUserAccounts}
                  getDataStaticFilters={{ hasViewAccount, user, companyAdminId }}
                  required
                  onChange={(account: any) => setPanelField("fromAccountId", account)}
                  style={{ width: 280 }}
                />
                {/* SelectField's inner TextField carries its own bottom margin (mb: 2) that
                    ActionButton doesn't — match it here so the two sit on the same baseline. */}
                <Box sx={{ mb: 2 }}>
                  <ActionButton buttonText="Pay" onButtonClick={handlePayClick} loading={paying} />
                </Box>
              </Box>
            )}
          </CardLayout>
        )}
      </Box>

      <FormDialog
        open={payConfirmOpen}
        onClose={() => setPayConfirmOpen(false)}
        onSubmit={() => {}}
        title="Confirm Payment"
        actions={
          <>
            <ActionButton buttonText="Cancel" variant="outlined" onButtonClick={() => setPayConfirmOpen(false)} />
            <ActionButton buttonText="Confirm & Pay" onButtonClick={handlePay} loading={paying} />
          </>
        }
      >
        <Typography variant="body2">
          Paying {selectedProject?.name} will finalize {unpaidRowCount} worker entr{unpaidRowCount === 1 ? "y" : "ies"} for this
          project — they cannot be edited afterward. Continue?
        </Typography>
      </FormDialog>
    </PageLayout>
  );
};
